import { Box, Inline, xcss } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { LayoutSelectedEventData } from '@databases/analytics/src/types';
import { SelectableButton } from '@databases/atlaskit/SelectableButton';
import { useDatabaseContent } from '@databases/database-state';
import { getFlagValue } from '@databases/feature-flags/manager';
import { useFlaggingCallback } from '@databases/flags';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { ViewChangeTypes } from '@databases/utils/database/view/view';
import React, { FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { getLayoutIcons } from '../../utils/utils';

const baseStyles = xcss({
  borderRadius: 'border.radius.100',
  padding: 'space.100',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  minWidth: 'space.1000',
});

export interface ViewSettingsLayoutTypePickerProps {
  isCompact: boolean;
  source: LayoutSelectedEventData['source'];
}

const ViewSettingsLayoutTypePicker: FunctionComponent<
  ViewSettingsLayoutTypePickerProps
> = ({ isCompact, source }) => {
  const { formatMessage } = useIntl();
  const { view, setViewChange } = useView();
  const { contentId: rawContentId } = useDatabaseContent();
  const contentId = rawContentId === null ? undefined : rawContentId;
  const client = useAnalytics();

  const handleLayoutTypeChange = useFlaggingCallback(
    async (type: DatabaseLayoutType, source: LayoutSelectedEventData['source']) => {
      setViewChange({
        type: ViewChangeTypes.LAYOUT_TYPE,
        value: type as DatabaseLayoutType,
      });

      if (contentId) {
        await client.sendLayoutSelectedEvent({
          layoutType: type,
          contentId,
          databaseContainer: getDatabaseContainerType(),
          source,
        });
      }
    },
    [client, contentId, setViewChange]
  );

  const layoutTypes = Object.values(DatabaseLayoutType);
  if (!getFlagValue('databases-frontend-layout-calendar', false)) {
    layoutTypes.splice(layoutTypes.indexOf(DatabaseLayoutType.CALENDAR), 1);
  }

  return (
    <Inline
      space={isCompact ? 'space.050' : 'space.100'}
      xcss={xcss({ flexWrap: 'wrap' })}
    >
      {layoutTypes.map((type) => {
        const layoutIcons = getLayoutIcons(type, 'medium');
        const isSelected = view.settings.layout.type === type;
        return (
          <Box
            key={type}
            xcss={xcss({
              flex: 1,
            })}
          >
            <SelectableButton
              $isSelected={isSelected}
              appearance="subtle"
              spacing="none"
              onClick={() => handleLayoutTypeChange(type, source)}
              shouldFitContainer
            >
              <Inline
                alignBlock="center"
                alignInline="center"
                space="space.050"
                xcss={[
                  baseStyles,
                  isSelected
                    ? xcss({
                        borderColor: 'color.border.selected',
                      })
                    : xcss({
                        borderColor: 'color.border',
                      }),
                ]}
              >
                {layoutIcons.icon}
                {formatMessage(layoutIcons.label)}
              </Inline>
            </SelectableButton>
          </Box>
        );
      })}
    </Inline>
  );
};

export default ViewSettingsLayoutTypePicker;
